import { Box, Card, CardContent, CardHeader, CircularProgress, Stack, Typography } from "@mui/material";
import { COLORS } from "assets";
import { alertTypeLabelKeyAt, MODEL_CHART_COLORS, MODELS } from "config/constants";
import useTranslation from "hooks/useTranslation";
import { useMemo } from "react";
import { ModelTypes, ThreatModel } from "types";
import { getThresholdSeries } from "./thresholdStats";

interface IBar {
  threats?: ThreatModel[];
  loading?: boolean;
  period: string;
  models?: ModelTypes[];
}

export default function Bar({ period, loading, threats, models = [] }: IBar) {
  const t = useTranslation();
  const series = useMemo(() => getThresholdSeries(threats, models), [threats, models]);

  return (
    <Card
      elevation={0}
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        borderRadius: `${COLORS.radius.lg}px`,
        bgcolor: COLORS.surface.card,
        border: `1px solid ${COLORS.surface.border}`,
        boxShadow: COLORS.shadow.card
      }}
    >
      <CardHeader
        title={(threats?.length || 0) + " " + t["Events Detected"]}
        subheader={t["Count of each threat compared to its threshold."]}
        action={
          <Typography
            variant="caption"
            sx={{ color: COLORS.surface.mutedText, fontWeight: 600, pt: 0.5, fontSize: 12 }}
          >
            {period}
          </Typography>
        }
        titleTypographyProps={{
          fontWeight: 600,
          sx: { color: COLORS.surface.primaryText, fontSize: 16 },
          letterSpacing: "-0.01em"
        }}
        subheaderTypographyProps={{
          sx: { color: COLORS.surface.secondaryText, fontSize: 13, mt: 0.25 }
        }}
        sx={{
          px: 2.5,
          pt: 2.5,
          pb: 0,
          alignItems: "flex-start"
        }}
      />
      <CardContent sx={{ flex: 1, px: 2.5, pt: 2, pb: 2.5, "&:last-child": { pb: 2.5 } }}>
        <Stack spacing={2.25} justifyContent="space-evenly" height="100%" minHeight={180}>
          {loading ? (
            <Box display="flex" justifyContent="center" alignItems="center" flex={1}>
              <CircularProgress size={24} />
            </Box>
          ) : (
            series.map((item, i) => {
              const modelIndex = MODELS.indexOf(models[i]);
              const fill = item > 0 ? MODEL_CHART_COLORS[modelIndex] : COLORS.surface.border;
              return (
                <Box key={models[i]}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center" mb={0.75}>
                    <Typography
                      noWrap
                      sx={{ color: COLORS.surface.primaryText, fontSize: 13, fontWeight: 500 }}
                    >
                      {t[alertTypeLabelKeyAt(modelIndex)]}
                    </Typography>
                    <Typography sx={{ color: COLORS.surface.secondaryText, fontSize: 12, fontWeight: 600 }}>
                      {Math.round(item)}%
                    </Typography>
                  </Stack>
                  <Box
                    height={8}
                    borderRadius="4px"
                    bgcolor={COLORS.surface.track}
                    overflow="hidden"
                  >
                    <Box
                      width={`${Math.max(item, 0)}%`}
                      height="100%"
                      borderRadius="4px"
                      bgcolor={fill}
                      sx={{ transition: "width 280ms ease", minWidth: item > 0 ? 4 : 0 }}
                    />
                  </Box>
                </Box>
              );
            })
          )}
        </Stack>
      </CardContent>
    </Card>
  );
}
